/* eslint-disable max-lines */
import { createSelector } from 'reselect';
import { ActionType, createAction, createAsyncAction, createReducer, RootState } from 'typesafe-actions';
import { put, select, cancelled, call } from 'redux-saga/effects';
import log from 'loglevel';
import { getEnv, getPathname, getRegion } from 'common/selectors';
import { api } from 'api';
import { patchUrlByLocale } from 'utils';
import { EffectR } from 'types/utils';
import { ModelJson, SEOTemplates, SeoTemplateVariables } from 'types/page-model';
import {
  getDisplayedName,
  getIsPricesSwapped,
  getPrice,
  getSecondPrice,
} from 'features/product-info/ducks/selectors';
import { selectUom, Price, SecondPrice } from 'features/product-info';
import { fetchModel } from './api';
import {
  AllowedSEOTemplates,
  GetModelResponse,
  PopulateSeoTemplatesFor,
  SEOProperties,
  Breadcrumb,
} from '../types';
import { populateSeoTemplateString } from './utils';
import { getBreadcrumbs, getModelJson, rootSelector } from '../selectors';

export const FETCH_MODEL = createAsyncAction(
  'pageModel/FETCH_MODEL/request',
  'pageModel/FETCH_MODEL/success',
  'pageModel/FETCH_MODEL/failture',
  'pageModel/FETCH_MODEL/cancel',
)<void, GetModelResponse, Error | string, void>();

export const RESET = createAction('pageModel/RESET')<void>();

export type ApplySEOPropertiesPayload = Partial<Pick<SEOProperties, PopulateSeoTemplatesFor>>;
export const APPLY_SEO_PROPERTIES = createAction(
  'pageModel/APPLY_SEO_PROPERTIES',
)<ApplySEOPropertiesPayload>();

export function* doFetchModel() {
  const pathname = (yield select(getPathname)) as EffectR.Select<typeof getPathname>;
  const region = (yield select(getRegion)) as EffectR.Select<typeof getRegion>;
  const requiredFields = [
    'analytics',
    'breadcrumbs',
    'experiments',
    'properties',
    'seoProperties',
    'brand',
    'regions',
    'warningMessage',
    'header',
  ].join();

  const abortController = (yield call([api, 'abortController'])) as EffectR.Call<
    (typeof api)['abortController']
  >;
  const { requestID } = (yield select(getEnv)) as EffectR.Select<typeof getEnv>;
  yield put(FETCH_MODEL.request());
  yield call(log.info, 'fetchModel was requested', 'requestID: ', requestID, 'dateNow: ', Date.now());

  try {
    const modelJson = (yield call(
      fetchModel,
      { pathname, regionCode: region, requiredFields },
      { cancelToken: abortController.token },
    )) as EffectR.Call<typeof fetchModel>;

    yield put(FETCH_MODEL.success(modelJson));

    yield call(log.info, 'fetchModel was loaded', 'requestID: ', requestID, 'dateNow: ', Date.now());
  } catch (e) {
    if (e instanceof Error) {
      yield put(FETCH_MODEL.failure(e));
      yield call(
        log.info,
        'fetchModel has error',
        'requestID: ',
        requestID,
        'dateNow: ',
        Date.now(),
        'e',
        e,
      );
    }
  } finally {
    if ((yield cancelled()) as boolean) {
      abortController.cancel();
      yield call(log.info, 'fetchModel was cancelled', 'requestID: ', requestID, 'dateNow: ', Date.now());
      yield put(FETCH_MODEL.cancel());
    }
  }
}

type ModelReducerActions =
  | ActionType<typeof FETCH_MODEL.success>
  | ActionType<typeof FETCH_MODEL.failure>
  | ActionType<typeof FETCH_MODEL.cancel>
  | ActionType<typeof RESET>
  | ActionType<typeof APPLY_SEO_PROPERTIES>;
export const model = createReducer<GetModelResponse | null, ModelReducerActions>(null)
  .handleAction(
    [
      // FETCH_MODEL.request,
      FETCH_MODEL.failure,
      FETCH_MODEL.cancel,
      RESET,
    ],
    () => null,
  )
  .handleAction(FETCH_MODEL.success, (_, { payload }) => payload)
  .handleAction(APPLY_SEO_PROPERTIES, (state, { payload }) => {
    if (state === null) return state;
    const { modelJson, ...st } = state;
    const { seoProperties } = modelJson ?? {};
    return {
      ...st,
      modelJson: {
        ...modelJson,
        seoProperties: {
          ...seoProperties,
          ...payload,
        },
      } as ModelJson,
    };
  });

export const loading = createReducer(false)
  .handleAction([FETCH_MODEL.success, FETCH_MODEL.failure, FETCH_MODEL.cancel, RESET], () => false)
  .handleAction(FETCH_MODEL.request, () => true);

export const error = createReducer<Response | Error | string | null>(null)
  .handleAction([FETCH_MODEL.request, FETCH_MODEL.success, FETCH_MODEL.cancel, RESET], () => null)
  .handleAction(FETCH_MODEL.failure, (_, { payload }) => {
    return payload instanceof Error ? payload.message : payload;
  });

export const isLoading = createSelector(rootSelector, (v) => v.loading);
export const getError = createSelector(rootSelector, (v) => v.error);
export const isModelFetched = createSelector(rootSelector, (v) => v.model !== null);
export const getBrandProperties = createSelector(getModelJson, (m) => m?.brand ?? null);
export const doesItHaveBrandsPage = createSelector(
  getBrandProperties,
  (b) =>
    b !== null &&
    typeof b.logo === 'object' &&
    b.logo !== null &&
    typeof b.label === 'string' &&
    typeof b.pagePath === 'string' &&
    b.pagePath.length > 0,
);
export const getBrandTitle = createSelector(getBrandProperties, (b) => b?.label);
export const getBrandPageLink = createSelector(getBrandProperties, (b) => b?.pagePath);
export const getBrandLogoImgSrc = createSelector(getBrandProperties, (b) => b?.logo.originalLink);

export const getGtmCode = createSelector(getModelJson, (m) => m?.analytics?.gtmCode);
export const getYaCode = createSelector(getModelJson, (m) => m?.analytics?.yaCode);

export const getVisibility = createSelector(rootSelector, (r) => r?.model?.visibilityData?.visibility);

export const getSeoTextHTML = createSelector(getModelJson, (m) => m?.seoProperties.seotext ?? undefined);

export const getSeoH1Text = createSelector(getModelJson, (mj) => mj?.seoProperties.h1);

export const getRegions = createSelector(getModelJson, (m) => m?.regions);

export const getRegionId = createSelector(getModelJson, (m) => m?.properties.regionId);

export const getRegionName = createSelector(getRegions, getRegionId, (regions, regionId) => {
  return regions?.find((region) => region.regionId === regionId)?.regionName;
});

export const getSeoProperties = (state: RootState): SEOProperties | undefined => {
  return state.pdp.pageModel.model?.modelJson?.seoProperties;
};

export function* selectBreadcrumbs() {
  return ((yield select(getBreadcrumbs)) as EffectR.Select<typeof getBreadcrumbs>) ?? null;
}

export function* doPopulateSEOTemplates() {
  const seoTemplates: SEOTemplates = yield select(getSeoProperties);
  const mainPrice = (yield select(getPrice)) as Price;
  const breadcrumbs = ((yield call(selectBreadcrumbs)) as Breadcrumb[]) || [];
  const category = breadcrumbs[breadcrumbs.length - 1] || {};
  const categoryLabel = category.label?.toLowerCase();
  const pathName = (yield select(getPathname)) as string;
  const categoryLink = patchUrlByLocale(category.link, pathName);
  const isPricesSwapped = (yield select(getIsPricesSwapped)) as boolean;
  const secondPrice = (yield select(getSecondPrice)) as SecondPrice;
  const uom = (yield call(selectUom)) as string;
  const price = `${isPricesSwapped ? secondPrice?.price : mainPrice?.main_price} ₽/${uom}`;

  if (typeof seoTemplates === 'object' && seoTemplates) {
    const variables = new Map<SeoTemplateVariables, string>([
      ['displayedName', ((yield select(getDisplayedName)) as EffectR.Select<typeof getDisplayedName>) ?? ''],
      ['price', price],
      ['parent', categoryLabel],
      ['parent_link', `<a href="${categoryLink}">${categoryLabel}</a>`],
    ]);

    if (categoryLabel === 'product details page') {
      variables.set('parent', '');
      variables.set('parent_link', '');
    }

    const stringVariables = new Map([[`цене 0 ₽/${uom}`, 'низкой цене']]);

    if (!mainPrice?.main_price) {
      stringVariables.set('@parent;', '');
      stringVariables.set('@parent_link;', '');
    }

    const payload = AllowedSEOTemplates.reduce((prev, templateKey) => {
      const tmpl = seoTemplates[templateKey];
      if (typeof tmpl === 'string') {
        prev[templateKey] = populateSeoTemplateString(tmpl, variables, stringVariables);
      }

      return prev;
    }, {} as ApplySEOPropertiesPayload);

    yield put(APPLY_SEO_PROPERTIES(payload));
  }
}
